import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X, Save, RotateCcw } from 'lucide-react';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const FormContainer = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 14px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows.large};
`;

const Header = styled.div`
  padding: 18px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    margin: 0;
    font-family: ${({ theme }) => theme.fonts.display};
    font-style: italic;
    font-weight: 500;
    font-size: 1.4rem;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.muted};
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  transition: color 160ms ease, background 160ms ease;

  &:hover {
    background: ${({ theme }) => `rgba(${theme.colors.textRgb}, 0.06)`};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const FormContent = styled.div`
  padding: 24px 28px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  label {
    display: block;
    margin-bottom: 6px;
    color: ${({ theme }) => theme.colors.text.primary};
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: 0.9rem;
    font-weight: 500;
  }

  .description {
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.text.muted};
    margin-bottom: 10px;
    line-height: 1.5;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 420px;
  background: ${({ theme }) => theme.colors.bg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 14px 16px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.mono};
  font-size: 0.86rem;
  line-height: 1.65;
  resize: vertical;
  outline: none;
  transition: border-color 160ms ease;

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 24px;
`;

const Button = styled.button`
  padding: 11px 20px;
  border-radius: 8px;
  font-family: ${({ theme }) => theme.fonts.sans};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: opacity 160ms ease, background 160ms ease, border-color 160ms ease;

  ${({ theme, primary }) => primary ? `
    background: ${theme.colors.accent};
    border: 1px solid ${theme.colors.accent};
    color: #fff;

    &:hover { opacity: 0.9; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  ` : `
    background: transparent;
    border: 1px solid ${theme.colors.border};
    color: ${theme.colors.text.secondary};

    &:hover {
      background: rgba(${theme.colors.textRgb}, 0.04);
      color: ${theme.colors.text.primary};
      border-color: ${theme.colors.borderStrong};
    }
  `}
`;

const DEFAULT_PROMPT = `You are Deniz Eren Gencay, the founder and lead software engineer of CND Software.
You are chatting with a visitor on your portfolio website.

**Your Goal:**
Help the visitor understand CND Software's services and encourage them to schedule a free consultation call.

**Your Persona:**
- Professional, friendly, and enthusiastic about technology.
- Expert in Full Stack Development (React, Node.js, Python), Mobile Apps (React Native), and AI Integration.
- Concise: Keep responses short (1-3 sentences) as this is a small chat bubble.

**Key Information:**
- **Services:** Custom Web Development, Mobile Applications, AI/LLM Integration, E-commerce Solutions.
- **Portfolio:** You have worked on projects like "Aitopia" (AI), "Vivoo" (Wellness), and "DeFactoFit".
- **Call to Action:** The best next step is always to "Schedule a Call" via the button in the Contact section.

**Guidelines:**
1. **Pricing:** If asked about price, say it depends on the project scope and suggest a quick call to get a quote.
2. **Technical:** You can answer technical questions to demonstrate expertise, but keep it accessible.
3. **Identity:** Never break character. You are Deniz (or his AI assistant).
4. **Safety:** Do not generate harmful, offensive, or illegal content.
5. **Conciseness:** Be brief. No long paragraphs.`;

const ChatSettings = ({ onClose, embedded = false }) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const db = getFirestore();

  useEffect(() => {
    const fetchPrompt = async () => {
      try {
        const docRef = doc(db, 'config', 'chat');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setPrompt(docSnap.data().systemPrompt);
        } else {
          setPrompt(DEFAULT_PROMPT);
        }
      } catch (error) {
        console.error("Error fetching prompt:", error);
        setPrompt(DEFAULT_PROMPT);
      } finally {
        setLoading(false);
      }
    };

    fetchPrompt();
  }, [db]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'chat'), {
        systemPrompt: prompt,
        updatedAt: new Date()
      });
      if (embedded) {
        setSavedMsg('Saved.');
        setTimeout(() => setSavedMsg(''), 2500);
      } else if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error("Error saving prompt:", error);
      alert("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm("Are you sure you want to reset to the default prompt?")) {
      setPrompt(DEFAULT_PROMPT);
    }
  };

  const body = (
    <FormContent>
      <FormGroup>
        <label>System Prompt</label>
        <div className="description">
          This prompt defines the persona, knowledge, and behavior of the AI chatbot.
          Be specific about what it should and shouldn't do.
        </div>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', opacity: 0.5, fontSize: '0.9rem' }}>Loading…</div>
        ) : (
          <TextArea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            spellCheck={false}
          />
        )}
      </FormGroup>

      <ButtonGroup>
        {embedded && savedMsg && (
          <span style={{ alignSelf: 'center', fontSize: '0.85rem', opacity: 0.7 }}>{savedMsg}</span>
        )}
        <Button onClick={handleReset} disabled={loading || saving}>
          <RotateCcw size={18} />
          Reset to Default
        </Button>
        <Button primary onClick={handleSave} disabled={loading || saving}>
          <Save size={18} />
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </ButtonGroup>
    </FormContent>
  );

  if (embedded) {
    return body;
  }

  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <FormContainer
        onClick={e => e.stopPropagation()}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
      >
        <Header>
          <h2>Chat AI Settings</h2>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        {body}
      </FormContainer>
    </Overlay>
  );
};

export default ChatSettings;
